// The body blueprint posing a multi-region splat's part rig.
// Notes: ~/comments/aos-threejs-poc/BodyDrive_gated_by_the_plan.md

import { useEffect, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { graphState } from '../../lib/graphState.js'
import { evaluateGraph, updateSMTransitions } from '../../lib/GraphEvaluator.js'
import { debugState } from '../../lib/debugState.js'
import { partDriveRegistry } from '../../ogs/preview/partDriveRegistry.js'
import { listAnimationAssets } from '../../lib/assetManagerApi.js'
import { setLiveBodyRig, isLiveBodyRigWanted } from '../../lib/liveBodyRig.js'
import { gestureEnvelope, stopBodyGesture } from '../../lib/GestureChannel.js'
import { movement } from '../../lib/movementComponent.js'
import { resolveAvatarTransformTarget } from '../../lib/avatarTransformTarget.js'
import { setVariable } from '../../lib/animVariables.js'
import { loadBodyClipRows, isGraphAuthored, createPartRigDriver } from '../../ogs/inference/multipartBodyDrive.js'

/** How often the attach poll asks: the drive registers during boot, after this effect. */
const ATTACH_POLL_MS = 250

/** Fetch each row's GLB and take its one clip, renamed to the row's name. */
async function loadClipAnimations(rows, log) {
  const loader = new GLTFLoader()
  const loaded = await Promise.all(rows.map((row) => new Promise((resolve) => {
    loader.load(row.url, (gltf) => {
      const clip = gltf.animations?.[0]
      if (!clip) { log.warn(`'${row.name}' carries no animation clip — skipped`); return resolve(null) }
      clip.name = row.name
      const marker = gltf.scene?.userData?.aosGeneratedMotion
      if (marker) clip.userData = { ...clip.userData, aosGeneratedMotion: marker }
      resolve(clip)
    }, undefined, (e) => {
      log.warn(`'${row.name}' failed to load — skipped: ${e?.message ?? e}`)
      resolve(null)
    })
  })))
  return loaded.filter(Boolean)
}

/** Evaluate the BODY graph and mirror its weights, so the editor's bars move. */
function evaluateBodyGraph() {
  updateSMTransitions(graphState)
  const out = evaluateGraph(graphState)
  debugState.bodyWeights = out.weights ?? {}
  debugState.clipWeights = out.weights ?? {}
  debugState.tick++
  return out
}

/** Clear an elapsed override, running its release hook — the GLB path's contract. */
function releaseDirectPlay(directPlay, log) {
  window.__DIRECT_PLAY__ = null
  if (typeof directPlay?.onExpire !== 'function') return
  try { directPlay.onExpire() } catch (e) { log.warn('DIRECT_PLAY onExpire threw:', e) }
}

export default function BodyDrive({ slug, getPlan, log }) {
  const driver = useRef(null)
  const rig = useRef(null)
  // A GETTER, not a value: the plan changes without re-rendering this component, so a
  // captured one would freeze at whatever the first frame saw.
  const planRef = useRef(getPlan)
  planRef.current = getPlan

  useEffect(() => {
    let cancelled = false
    let attaching = false // the poll re-fires mid-attach and would re-download every clip

    // A preview asks for the drive as much as an authored graph does — and so does a
    // generated clip, which must BAKE against this rig to exist at all.
    const wanted = () => planRef.current()?.bodyDrive?.enabled !== false && (
      (planRef.current()?.bodyDrive?.source !== 'preview-only' && isGraphAuthored(graphState))
      || !!window.__DIRECT_PLAY__?.clipName
      || isLiveBodyRigWanted()
    )

    const attach = async () => {
      if (attaching || !partDriveRegistry.isAvailable() || !wanted()) return false
      attaching = true
      try {
        const drive = await partDriveRegistry.build()
        if (cancelled) return false
        // Null is a build that FAILED (the cause is on the console), not "not yet" —
        // retrying would re-download the rig every tick, forever.
        if (!drive) throw new Error('the part drive did not build — see the error above')
        const clips = await loadBodyClipRows(slug, listAnimationAssets)
        if (cancelled) return false
        const added = drive.rig.addClips(await loadClipAnimations(clips, log))
        if (cancelled) return false
        rig.current = drive.rig
        driver.current = createPartRigDriver({
          rig: drive.rig, drive, evaluate: evaluateBodyGraph,
          warn: (...a) => log.warn(...a), info: (...a) => log.info(...a),
        })
        setLiveBodyRig({
          scene: drive.rig.scenes[0],
          restPose: drive.rig.restPose,
          rigToWorld: drive.rigToWorld ?? null,
          rigToWorldScale: drive.rigToWorldScale ?? null,
          register: (_name, clip, { additive = false, generatedMarker } = {}) => drive.rig.addClips([clip], {
            additiveInPlace: additive,
            replace: true,
            generatedMarker: generatedMarker !== undefined
              ? generatedMarker
              : (clip.userData?.aosGeneratedMotion ?? { keepRootMotion: false }),
          }),
          clipDuration: (name) => drive.rig.clipDuration(name),
        })
        log.info(`body blueprint owns the part drive — ${added.length} clips: ${added.join(', ') || 'none'}`)
        return true
      } finally { attaching = false }
    }

    const poll = setInterval(() => {
      attach().then((done) => { if (done) clearInterval(poll) }).catch((e) => {
        clearInterval(poll)
        log.error('body drive failed to attach', e)
      })
    }, ATTACH_POLL_MS)

    return () => {
      cancelled = true
      clearInterval(poll)
      // A surviving handle would pose a disposed mixer on the next character's frame 1.
      driver.current = null
      rig.current = null
      setLiveBodyRig(null)
    }
  }, [slug, log])

  useEffect(() => {
    window.__MULTIPART_DRIVE__ = {
      get attached() { return !!rig.current },
      get owner() { return driver.current?.owner ?? 'none' },
      get clipNames() { return rig.current?.clipNames() ?? [] },
    }
    return () => { window.__MULTIPART_DRIVE__ = null }
  }, [])

  useFrame((_, delta) => {
    const active = driver.current
    if (!active) return
    const result = active.frame({
      delta,
      now: Date.now(),
      graph: graphState,
      directPlay: window.__DIRECT_PLAY__,
      envelope: gestureEnvelope(),
    })
    // The root travels exactly as far as the clip has been posed — ONE clock for both.
    if (result.owner === 'override' && planRef.current()?.bodyDrive?.rootMotion !== false) {
      window.__DIRECT_PLAY__?.rootMotion?.placeAt(result.progress)
    }
    if (result.expiredDirectPlay) releaseDirectPlay(window.__DIRECT_PLAY__, log)
    if (result.clearGesture) stopBodyGesture()
  })

  return null
}

/** Point the movement component at the splat root and publish its ground speed. */
export function SplatMovementTarget() {
  useEffect(() => {
    movement.setSpeedSink((speed) => setVariable('Speed', speed))
    return () => {
      movement.setSpeedSink(null)
      setVariable('Speed', 0)
      if (movement.getGroup() === window.__OGS_AVATAR_ROOT_NEXT__) movement.setGroup(null)
    }
  }, [])

  // Resolved per frame: the root publishes during boot, after any effect.
  useFrame(() => {
    const target = resolveAvatarTransformTarget()
    if (target !== movement.getGroup()) movement.setGroup(target)
  })

  return null
}
